import type { CityConfig } from "@/pages/city-template/types";

export const florenceSCConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Florence",
  state: "South Carolina",
  stateAbbr: "SC",
  slug: "florence-sc",
  route: "/cities/florence-sc",
  title: "Emergency & Expedited Passport Services Florence SC | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Florence, SC — as fast as 24 hours. No Florence passport agency. Fast Passport Center couriers to the Atlanta Passport Agency at 230 Peachtree St NW. A+ BBB rated. Serving Pee Dee travelers, Francis Marion University students, McLeod Health professionals, and I-95 corridor families.",
  h1: "Emergency & Expedited Passport Services in Florence, SC",
  heroSubtitle: "Florence is the hub of the Pee Dee region, sitting at the crossroads of I-95 and I-20 in eastern South Carolina. Fast Passport Center's registered couriers deliver your documents to the Atlanta Passport Agency at 230 Peachtree St NW so you get a new or renewed passport as fast as 24 hours — whether you're a Francis Marion University student preparing for study abroad, a McLeod Regional Medical Center professional heading to an international conference, or a Darlington County family planning an overseas vacation from Florence Regional Airport.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Florence%20South%20Carolina%20historic%20downtown%20district%20with%20beautifully%20preserved%20brick%20storefronts%20along%20Evans%20Street%20lined%20with%20crepe%20myrtle%20trees%20in%20full%20pink%20bloom%20during%20golden%20hour%2C%20traditional%20southern%20small%20city%20charm%20with%20clock%20tower%20and%20charming%20streetscapes%2C%20warm%20sunlight%20casting%20long%20shadows%20on%20red%20brick%20architecture&width=1800&height=700&seq=florence-sc-hero-001&orientation=landscape",
  heroImageAlt: "Florence South Carolina historic downtown district with brick storefronts and crepe myrtle at golden hour",
  geoPosition: "34.1954;-79.7628",
  geoPlacename: "Florence, South Carolina",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "Atlanta Passport Agency",
    address: "230 Peachtree St NW, Suite 1000, Atlanta, GA 30303",
    distance: "Approximately 290 miles from Florence — about 4 hours 15 minutes via I-20",
  },
  nearbyCitiesZips: [
    { name: "Florence", zips: "29501, 29505, 29506" },
    { name: "Darlington", zips: "29532" },
    { name: "Hartsville", zips: "29550" },
    { name: "Marion", zips: "29571" },
    { name: "Lake City", zips: "29560" },
    { name: "Timmonsville", zips: "29161" },
    { name: "Pamplico", zips: "29583" },
    { name: "Johnsonville", zips: "29555" },
    { name: "Dillon", zips: "29536" },
    { name: "Bennettsville", zips: "29512" },
    { name: "Cheraw", zips: "29520" },
    { name: "Bishopville", zips: "29010" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Atlanta Passport Agency — serving Florence and the Pee Dee region" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Pee Dee Region Hub", desc: "Serving Florence, Darlington, Hartsville, Marion, and all Florence County" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Florence case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Charleston SC", route: "/cities/charleston" },
    { name: "Expedited passport in Columbia SC", route: "/cities/columbia-sc" },
    { name: "Urgent passport in Florence SC", route: "/cities/florence-sc" },
    { name: "South Carolina passport hub", route: "/states/south-carolina" },
    { name: "Fast passport in Myrtle Beach SC", route: "/cities/myrtle-beach" },
    { name: "Expedited passport in Greenville SC", route: "/cities/greenville-sc" },
    { name: "Emergency passport in Summerville SC", route: "/cities/summerville" },
  ],
  fpcOfficeAddress: "230 Peachtree St NW, Suite 1000, Atlanta, GA 30303",
  fpcOfficeNote: "Fast Passport Center serves Florence remotely via secure courier. Our registered couriers hand-deliver your documents to the Atlanta Passport Agency at 230 Peachtree St NW — approximately 290 miles away. Florence's location at the I-95/I-20 crossroads makes overnight courier routing highly reliable, and our dedicated agents ensure Pee Dee travelers never need to make the 4-hour-plus drive to Atlanta.",
  fpcOfficeDistance: "Serving all of the Pee Dee region",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Florence, Darlington, Hartsville, Marion, Lake City, and the I-95 corridor. From Francis Marion University students heading abroad to McLeod Health professionals attending international conferences, we help Pee Dee travelers get their passports fast.",
  nearbyCitiesIntro: "We serve Florence and nearby communities including Darlington, Hartsville, Marion, Lake City, Timmonsville, Dillon, and the entire Pee Dee region.",
  howItWorksIntro: "Most Florence travelers complete everything online and ship documents overnight. Fast Passport Center provides fast courier turnaround for Pee Dee residents — eliminating the 4-hour-plus drive to the Atlanta Passport Agency.",
  mistakesIntro: "Florence travelers, including Francis Marion University students studying abroad and McLeod Regional Medical Center healthcare professionals heading to international conferences, face unique passport challenges. Here are the most common mistakes we see.",
  testimonialsIntro: "Florence and Pee Dee travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Florence? Here are the most common ones we hear from Pee Dee and I-95 corridor travelers.",
  statePageRoute: "/states/south-carolina",
  statePageName: "South Carolina",
  dmvWebsite: "https://www.scdmvonline.gov/",
  dmvLabel: "South Carolina DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins at 230 Peachtree St NW",
        "Life-or-death emergencies only",
        "290 miles to Atlanta (4 hours 15 minutes via I-20)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No drive or appointment needed",
        "Ideal for FMU students going abroad",
        "Perfect for McLeod Health conference travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from FLO",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Florence is 290 miles from the Atlanta Passport Agency — our courier service saves Pee Dee travelers the I-20 drive to Atlanta.",
      bullets: ["Pee Dee region coverage", "24–48 hour turnaround available", "No appointment or drive needed"],
      badge: "Fastest in Pee Dee",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Florence adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Florence and Florence County"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Florence and the Pee Dee region.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Florence, Darlington, and Pee Dee residents with DS-64 form guidance.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Atlanta agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Florence County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Florence and Pee Dee residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Florence SC", description: "Urgent passport processing for Florence, SC travelers via Atlanta Passport Agency courier — 290 miles from Florence." },
    { name: "Passport Renewal Florence SC", description: "Fast passport renewal for Florence and Florence County, South Carolina residents." },
    { name: "New Passport Florence SC", description: "First-time passport application assistance for Florence and Pee Dee area residents." },
    { name: "Lost Passport Florence SC", description: "Passport replacement for lost or stolen passports for Florence and Pee Dee residents." },
    { name: "Child Passport Florence SC", description: "Minor passport services for Florence and Pee Dee region families." },
    { name: "Name Change Passport Florence SC", description: "Passport name change service for Florence, SC residents." },
  ],
  testimonials: [
    {
      name: "Dr. Sarah P.",
      location: "McLeod Regional Medical Center",
      rating: 5,
      text: "I'm a cardiologist at McLeod Health and got invited to present at a European Society of Cardiology conference in Barcelona with 12 days notice. My passport had expired. Fast Passport Center renewed it in 3 business days. The courier service through Atlanta was seamless — never had to leave Florence.",
      service: "Emergency Renewal",
    },
    {
      name: "Marcus J.",
      location: "Francis Marion University Student",
      rating: 5,
      text: "I'm a biology major at FMU and was accepted to a research exchange program in Costa Rica. Realized 2 weeks before departure that I needed a new passport. Fast Passport Center got it done in 5 business days. Their team was so patient explaining everything to a first-time applicant.",
      service: "First-Time Passport",
    },
    {
      name: "Linda & Robert T.",
      location: "Darlington, SC",
      rating: 5,
      text: "Retired couple from Darlington planning a 50th anniversary cruise from Barcelona. We both needed renewals and had about 3 weeks. Fast Passport Center handled both applications simultaneously — received our new passports in 7 business days. Saved us the stress of worrying whether they'd arrive.",
      service: "Dual Renewal",
    },
    {
      name: "Tony W.",
      location: "Florence, SC — NASCAR Crew Chief",
      rating: 5,
      text: "Crew chief for a NASCAR Xfinity team based near Darlington. Got called up for a race in Mexico City with the Cup Series — needed an emergency passport in 4 days. Fast Passport Center came through. Had my passport in 2 business days. Unbelievable service for the Pee Dee area.",
      service: "Emergency Processing",
    },
  ],
  facilities: [
    {
      id: "flo-1",
      name: "Florence Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1901 W Evans St",
      city: "Florence",
      state: "SC",
      zip: "29501",
      phone: "(843) 662-2211",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Florence — West Evans Street",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Florence and Florence County residents. Appointment required. Located on West Evans Street near I-95.",
      icon: "ri-mail-line",
    },
    {
      id: "flo-2",
      name: "Florence County Clerk of Court",
      type: "County Clerk Passport Acceptance",
      address: "180 N Irby St, MSC-M",
      city: "Florence",
      state: "SC",
      zip: "29501",
      phone: "(843) 665-3031",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Florence — Downtown",
      website: "https://www.florenceco.org/",
      websiteLabel: "Florence County Government",
      notes: "County clerk passport acceptance services in the Florence County Judicial Center. Convenient downtown location near the Florence Civic Center area.",
      icon: "ri-government-line",
    },
    {
      id: "flo-3",
      name: "Darlington Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "90 S Main St",
      city: "Darlington",
      state: "SC",
      zip: "29532",
      phone: "(843) 393-3821",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Darlington — 10 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Darlington County residents and those near Darlington Raceway area. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "flo-4",
      name: "Hartsville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "115 S 4th St",
      city: "Hartsville",
      state: "SC",
      zip: "29550",
      phone: "(843) 332-9611",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Hartsville — 24 miles northwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Darlington County and Hartsville area residents including Coker University students. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "flo-5",
      name: "Marion Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "100 N Main St",
      city: "Marion",
      state: "SC",
      zip: "29571",
      phone: "(843) 423-2146",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Marion — 20 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Marion County and eastern Pee Dee residents. Call ahead — limited hours may vary.",
      icon: "ri-mail-line",
    },
    {
      id: "flo-6",
      name: "Lake City Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "134 Sauls St",
      city: "Lake City",
      state: "SC",
      zip: "29560",
      phone: "(843) 394-8471",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Lake City — 24 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for southern Florence County and Lake City area residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Atlanta Agency", agency: "290 miles — 4 hour 15 minute drive on I-20, but need appointment", fpc: "We drive for you — no appointment needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-car-line",
      title: "Assuming You Can Drive to Atlanta and Walk In",
      description: "At 290 miles away, Florence residents sometimes plan to 'drive to Atlanta and get it done.' But the Atlanta Passport Agency at 230 Peachtree St NW requires pre-scheduled NPIC appointments — absolutely no walk-ins. Appointments often fill 3–4 weeks in advance, especially during peak travel seasons.",
    },
    {
      icon: "ri-calendar-line",
      title: "FMU Students Waiting Until the Week Before Study Abroad",
      description: "Francis Marion University students accepted to summer study abroad programs often wait until the week before travel to handle passport applications — right when the Florence Post Office is busiest. Study abroad planning should start passport applications in March, not late April or May.",
    },
    {
      icon: "ri-global-line",
      title: "Confusing Student Visa Requirements With Passport Validity",
      description: "FMU students heading to semester-long programs in the UK, Spain, or Japan often confuse passport validity with visa requirements. A valid passport is not the same as a valid student visa. Some countries require passports valid 6 months beyond the visa end date.",
    },
    {
      icon: "ri-flag-line",
      title: "NASCAR Crew Members Not Checking Passports Before International Races",
      description: "The Pee Dee region is NASCAR country. Crew members and team personnel called up for international races in Mexico City, Montreal, or overseas exhibition events often discover expired passports days before departure. International racing requires the same passport validity rules as any other travel.",
    },
    {
      icon: "ri-image-line",
      title: "Using FMU Campus ID Photos as Passport Photos",
      description: "Francis Marion University students sometimes try to use university ID photos or self-taken phone photos for passport applications. Passport photos must be exactly 2x2 inches with a pure white background, neutral expression, and no glasses. Only professional passport photo services meet U.S. Department of State standards.",
    },
    {
      icon: "ri-restart-line",
      title: "Not Checking Passport Validity Before Booking From FLO or CHS",
      description: "Florence Regional Airport (FLO) offers limited commercial service, so most international travelers connect through Charlotte (CLT) or Charleston (CHS). Pee Dee travelers often book international flights months in advance without checking passport expiration dates. Most countries require 6 months validity beyond your return date.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Florence, Darlington, or Hartsville." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the Atlanta Passport Agency — 290 miles via I-20." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Atlanta Passport Agency via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Florence or Pee Dee address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is Florence from the Atlanta passport agency?",
      a: "Florence is approximately 290 miles from the Atlanta Passport Agency at 230 Peachtree St NW — about a 4 hour 15 minute drive southwest on I-20. Florence's location at the I-95/I-20 crossroads makes the route straightforward, but the Atlanta agency requires pre-scheduled appointments and does not accept walk-ins.",
    },
    {
      q: "Does Florence, SC have its own passport agency?",
      a: "No. Florence does not have a passport agency. The closest passport agency is the Atlanta Passport Agency at 230 Peachtree St NW, about 290 miles away. Florence only has passport acceptance facilities — the Florence Main Post Office on West Evans Street and the Florence County Clerk of Court — which can accept your DS-11 application but cannot process or expedite it.",
    },
    {
      q: "I'm a Francis Marion University student — how fast can I get a passport for study abroad?",
      a: "FMU students heading to study abroad programs in Europe, Central America, or Asia are frequent clients. Emergency processing delivers passports in 24–48 hours. Standard expedited processing runs 2–9 business days. We recommend starting the process at least 2–3 months before your program start date to allow time for visa processing after receiving your passport.",
    },
    {
      q: "Where do Florence travelers typically fly internationally?",
      a: "Florence Regional Airport (FLO) offers limited commercial service via American Eagle to Charlotte (CLT). Most Florence travelers drive to Charlotte Douglas International Airport (CLT, about 100 miles away) or Charleston International Airport (CHS, about 120 miles away) for international flights. Some also use Myrtle Beach International (MYR, about 70 miles away).",
    },
    {
      q: "How fast can I get an emergency passport in Florence?",
      a: "Emergency processing is typically 24–48 hours for Florence residents. Florence's location at the I-95/I-20 crossroads makes overnight courier routing highly reliable. Standard expedited processing runs 2–9 business days. Our courier service eliminates the need to drive 290 miles to Atlanta.",
    },
    {
      q: "Where can I get passport photos in Florence?",
      a: "Walgreens at 2150 W Evans St and CVS Pharmacy at 1953 W Palmetto St in Florence both offer passport photo services. There are also UPS Store locations in Florence. Always verify your photos meet exact 2x2 inch specifications with a white background — FMU campus photo services may not meet U.S. Department of State standards.",
    },
    {
      q: "Can the Florence County Clerk of Court process my passport?",
      a: "Yes. The Florence County Clerk of Court at 180 N Irby St in downtown Florence accepts passport applications as a passport acceptance facility. They can accept DS-11 applications and verify your documents. Call (843) 665-3031 before visiting to confirm current passport service hours and availability.",
    },
    {
      q: "How do I renew my passport in Florence?",
      a: "Adult renewals meeting all DS-82 eligibility criteria (16 or older, issued within 15 years, same name) can be done entirely by mail. Fast Passport Center's courier service achieves 2–9 business day turnaround — far faster than self-service USPS (8–11 weeks). If you're not eligible for mail-in renewal, visit the Florence Post Office or County Clerk for an in-person DS-11 application.",
    },
    {
      q: "Does the Florence Post Office take walk-in passport appointments?",
      a: "The Florence Main Post Office at 1901 W Evans St accepts passport applications by appointment Monday through Friday from 9:00 AM to 4:00 PM. Walk-ins are generally not accepted — call (843) 662-2211 to schedule. The post office is often busiest during lunch hours and late afternoons.",
    },
    {
      q: "Can Fast Passport Center help Darlington and Hartsville residents too?",
      a: "Absolutely. We serve all of the Pee Dee region including Florence, Darlington, Hartsville, Marion, Lake City, Timmonsville, Dillon, Bennettsville, Cheraw, and Bishopville. Residents of surrounding communities never need to drive to Florence or Atlanta for service — our courier handles everything.",
    },
    {
      q: "I'm a McLeod Health professional heading to a medical conference — what's the timeline?",
      a: "McLeod Regional Medical Center professionals heading to international medical conferences should start passport preparation 3–4 months before travel. Many destinations require both a valid passport and a visa. Fast Passport Center processes passports in 2–9 business days, but allow extra time for visa processing after you receive your passport.",
    },
    {
      q: "Is the Florence County Clerk faster than the Florence Post Office for passport applications?",
      a: "Both the Florence County Clerk of Court and the Florence Main Post Office serve as passport acceptance facilities with similar processing capabilities. The County Clerk's office on North Irby Street often has shorter wait times and more personalized service. However, neither facility can expedite processing — that's where Fast Passport Center's courier service to the Atlanta agency comes in.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Florence", address: "2150 W Evans St, Florence, SC 29501", hours: "Mon–Sun: 7AM–10PM", website: "https://www.walgreens.com/storelocator" },
    { name: "CVS Pharmacy — Florence", address: "1953 W Palmetto St, Florence, SC 29501", hours: "Mon–Sun: 8AM–9PM", website: "https://www.cvs.com/store-locator" },
    { name: "UPS Store — Florence", address: "1945 W Palmetto St, Florence, SC 29501", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–2PM", website: "https://www.theupsstore.com/tools/find-a-store" },
    { name: "Walmart Photo — Florence", address: "230 N Beltline Dr, Florence, SC 29501", hours: "Mon–Sun: 7AM–9PM", website: "https://www.walmart.com/store/finder" },
    { name: "Walgreens — Darlington", address: "109 Express Ln, Darlington, SC 29532", hours: "Mon–Sun: 7AM–10PM", website: "https://www.walgreens.com/storelocator" },
    { name: "CVS Pharmacy — Hartsville", address: "1157 S 4th St, Hartsville, SC 29550", hours: "Mon–Sun: 8AM–9PM", website: "https://www.cvs.com/store-locator" },
  ],
  dmvLocations: [
    { name: "SC DMV — Florence", address: "600 National Cemetery Rd, Florence, SC 29502", hours: "Mon–Fri: 8:30AM–5:00PM", note: "Primary SC DMV branch serving Florence County and the Pee Dee region. REAL ID, driver's license, and state ID services for Florence area residents.", featured: true },
    { name: "SC DMV — Darlington", address: "1 Public Square, Darlington, SC 29532", hours: "Mon–Fri: 8:30AM–5:00PM", note: "Serves Darlington County residents. Located in downtown Darlington near the historic courthouse square.", featured: false },
    { name: "SC DMV — Sumter", address: "1050 Broad St, Sumter, SC 29150", hours: "Mon–Fri: 8:30AM–5:00PM", note: "Serves Sumter County and southern Pee Dee area residents. About 45 miles south of Florence.", featured: false },
  ],
  airports: [
    {
      code: "FLO",
      name: "Florence Regional Airport",
      distance: "Florence — East Palmetto Street",
      address: "2100 Terminal Dr, Florence, SC 29501",
      note: "Pee Dee's regional airport with American Eagle service to Charlotte (CLT). Limited commercial service — most international travelers connect through CLT or drive to CHS for direct international options.",
      flightUrl: "https://www.flyflo.com/",
      flightLabel: "FLO Airport",
      featured: true,
    },
    {
      code: "CLT",
      name: "Charlotte Douglas International Airport",
      distance: "100 miles north of Florence",
      address: "5501 R C Josh Birmingham Pkwy, Charlotte, NC 28208",
      note: "Major international hub and closest airport with extensive international service to Florence. American Airlines hub with direct flights to Europe, Caribbean, and Latin America. About 1 hour 45 minutes via US-52 and I-485.",
      flightUrl: "https://www.cltairport.com/",
      flightLabel: "CLT Airport",
      featured: false,
    },
    {
      code: "CHS",
      name: "Charleston International Airport",
      distance: "120 miles south of Florence",
      address: "5500 International Blvd, North Charleston, SC 29418",
      note: "Growing airport with domestic routes and limited international service. About 2 hours via I-95 and I-26. Alternative for Pee Dee residents wanting to avoid Charlotte traffic.",
      flightUrl: "https://www.iflychs.com/",
      flightLabel: "CHS Airport",
      featured: false,
    },
    {
      code: "MYR",
      name: "Myrtle Beach International Airport",
      distance: "70 miles east of Florence",
      address: "1100 Jetport Rd, Myrtle Beach, SC 29577",
      note: "Coastal South Carolina airport with seasonal and year-round domestic routes. About 1 hour 15 minutes via US-501. Primarily domestic service with some seasonal international routes.",
      flightUrl: "https://www.flymyrtlebeach.com/",
      flightLabel: "MYR Airport",
      featured: false,
    },
  ],
};